import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import {
  ApiService,
  isSrReferral,
  Referral,
  ReferralSimulateContext,
  SimulationApiError,
  SimulationResponse,
  SimulationLocationResult,
  SimulationReferrerTypeResult,
  TemplateReferral,
} from '../services/apiService';
import { Button } from '../components/Button';
import { TriageCategoryBadge } from '../components/TriageCategoryBadge';
import './ReferralSimulatorPage.css';

const MAX_PDF_BYTES = 20 * 1024 * 1024;
const SEARCH_DEBOUNCE_MS = 300;

const DEFAULTS = {
  senderName: 'Dr Test Referrer',
  senderEmail: 'test.referrer@example.invalid',
  subject: 'Referral: Synthetic Patient Assessment',
  body: 'Please find attached a fully synthetic referral for pipeline testing.',
};

// AXISREF397: simulationIsBlocked and PRODUCTION_FRONTEND_HOST removed — simulator
// is now available in all environments; the admin-only route guard is the sole gate.

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'Not extracted';
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'Not extracted';
  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function firstValue(sources: Array<Record<string, unknown> | undefined>, keys: string[]): unknown {
  for (const source of sources) {
    for (const key of keys) {
      if (source?.[key] !== undefined && source[key] !== null && source[key] !== '') {
        return source[key];
      }
    }
  }
  return undefined;
}

/**
 * Flatten the pipeline's nested extracted structure into a single-level dict so
 * firstValue() can find fields regardless of which response shape was returned.
 *
 * Handles two shapes:
 *   1. Nested: { patient: { first_name: { value, confidence } }, referral: { ... } }
 *   2. Fields array: [{ field_name: "patient_first_name", field_value: "Toni" }]
 */
function flattenExtracted(extracted: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!extracted) return {};
  const flat: Record<string, unknown> = {};

  const set = (key: string, val: unknown) => {
    if (flat[key] === undefined || flat[key] === null || flat[key] === '') flat[key] = val;
  };

  // Shape 1: nested sections
  const SECTIONS = ['patient', 'referral', 'provider'];
  for (const section of SECTIONS) {
    const fields = extracted[section];
    if (typeof fields !== 'object' || fields === null || Array.isArray(fields)) continue;
    for (const [field, fieldData] of Object.entries(fields as Record<string, unknown>)) {
      const val =
        typeof fieldData === 'object' && fieldData !== null && 'value' in fieldData
          ? (fieldData as Record<string, unknown>).value
          : fieldData;
      set(`${section}_${field}`, val);
      set(field, val);
    }
  }

  // Shape 2: fields array [{ field_name, field_value }]
  const fieldsArr = extracted['fields'];
  if (Array.isArray(fieldsArr)) {
    for (const entry of fieldsArr as Array<Record<string, unknown>>) {
      const name = String(entry['field_name'] ?? '');
      const val = entry['field_value'];
      if (name && val !== undefined) set(name, val);
    }
  }

  return flat;
}

function validatePdf(candidate: File): string | null {
  const isPdf =
    candidate.type === 'application/pdf' || candidate.name.toLowerCase().endsWith('.pdf');
  if (!isPdf) return 'Only PDF files are accepted.';
  if (candidate.size > MAX_PDF_BYTES) return 'The PDF must be 20 MB or smaller.';
  return null;
}

interface ResultFieldProps {
  label: string;
  value: unknown;
}

const ResultField: React.FC<ResultFieldProps> = ({ label, value }) => (
  <div className="simulator-result-field">
    <dt>{label}</dt>
    <dd>{displayValue(value)}</dd>
  </div>
);

function ConfidenceBadge({ score }: { score: number | null }) {
  if (score === null || score === undefined) return <span className="sim-badge sim-badge--neutral">No score</span>;
  if (score >= 70) return <span className="sim-badge sim-badge--high">{score}%</span>;
  if (score >= 40) return <span className="sim-badge sim-badge--medium">{score}%</span>;
  return <span className="sim-badge sim-badge--low">{score}%</span>;
}

function SourceBadge({ source }: { source: string | null }) {
  if (!source) return null;
  if (source === 'patient_address')
    return <span className="sim-badge sim-badge--high">Patient address</span>;
  return <span className="sim-badge sim-badge--medium">Practice address (fallback)</span>;
}

function GeoMatchBadge({ matchType }: { matchType: string | null }) {
  if (!matchType || matchType === 'none') return <span className="sim-badge sim-badge--low">No match</span>;
  if (matchType === 'exact') return <span className="sim-badge sim-badge--high">Exact</span>;
  return <span className="sim-badge sim-badge--medium">Disambiguated</span>;
}

export const ReferralSimulatorPage: React.FC = () => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Files — multi-PDF support
  const [files, setFiles] = useState<File[]>([]);

  const [senderName, setSenderName] = useState(DEFAULTS.senderName);
  const [senderEmail, setSenderEmail] = useState(DEFAULTS.senderEmail);
  const [subject, setSubject] = useState(DEFAULTS.subject);
  const [body, setBody] = useState(DEFAULTS.body);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<{ message: string; stage?: string } | null>(null);
  const [duplicate, setDuplicate] = useState(false);
  const [result, setResult] = useState<SimulationResponse | null>(null);
  const [pipelineMode, setPipelineMode] = useState<'standalone' | 'pipeline'>('standalone');

  // Templates
  const [templates, setTemplates] = useState<TemplateReferral[]>([]);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [addTemplateName, setAddTemplateName] = useState('');
  const [addTemplateDesc, setAddTemplateDesc] = useState('');
  const [addTemplateSenderName, setAddTemplateSenderName] = useState(DEFAULTS.senderName);
  const [addTemplateSenderEmail, setAddTemplateSenderEmail] = useState(DEFAULTS.senderEmail);
  const [addTemplateSubject, setAddTemplateSubject] = useState(DEFAULTS.subject);
  const [addTemplateBody, setAddTemplateBody] = useState(DEFAULTS.body);
  const [addTemplateFiles, setAddTemplateFiles] = useState<File[]>([]);
  const [addTemplateSubmitting, setAddTemplateSubmitting] = useState(false);
  const [addTemplateError, setAddTemplateError] = useState<string | null>(null);
  const addTemplateInputRef = useRef<HTMLInputElement>(null);

  // Re-simulate from existing referral
  const [referralSearch, setReferralSearch] = useState('');
  const [referralSearchResults, setReferralSearchResults] = useState<Referral[]>([]);
  const [referralSearchLoading, setReferralSearchLoading] = useState(false);
  const [referralSearchOpen, setReferralSearchOpen] = useState(false);
  const [selectedReferralContext, setSelectedReferralContext] = useState<ReferralSimulateContext | null>(null);
  const [loadingReferralContext, setLoadingReferralContext] = useState(false);
  const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false);
  const [saveAsTemplateName, setSaveAsTemplateName] = useState('');
  const [saveAsTemplateSubmitting, setSaveAsTemplateSubmitting] = useState(false);
  const [saveAsTemplateError, setSaveAsTemplateError] = useState<string | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchAbortRef = useRef<AbortController | null>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  // Latest-wins guard: every call to selectReferralForResimulate increments this counter.
  // The async callback checks it before applying results so a fast A→B selection never
  // applies A's files or email context after B has already started loading.
  const resimulateSeqRef = useRef<number>(0);

  useEffect(() => {
    void ApiService.getTemplateReferrals().then(setTemplates).catch(() => {});
  }, []);

  // Close search dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setReferralSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleReferralSearchChange = useCallback((term: string) => {
    setReferralSearch(term);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!term.trim()) {
      setReferralSearchResults([]);
      setReferralSearchOpen(false);
      return;
    }
    searchDebounceRef.current = setTimeout(async () => {
      if (searchAbortRef.current) searchAbortRef.current.abort();
      searchAbortRef.current = new AbortController();
      setReferralSearchLoading(true);
      try {
        const data = await ApiService.getReferrals(
          1, 10, undefined, term, undefined, undefined, undefined, undefined,
          searchAbortRef.current.signal,
          undefined, undefined, undefined, undefined, undefined, undefined,
          'reception', 'name_nhi',
        );
        setReferralSearchResults(data.referrals);
        setReferralSearchOpen(true);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        setReferralSearchResults([]);
      } finally {
        setReferralSearchLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  const selectReferralForResimulate = async (referral: Referral) => {
    // Increment sequence so any in-flight earlier selection becomes stale
    const mySeq = ++resimulateSeqRef.current;

    setReferralSearchOpen(false);
    setReferralSearch(`${referral.patient_first_name ?? ''} ${referral.patient_last_name ?? ''}`.trim());

    // SR referrals go through a separate structured intake path — they don't have email/PDF
    // attachments that can be fed back through this PDF simulator.
    if (isSrReferral(referral)) {
      setError({
        message:
          'SR referrals use structured suburb/city metadata and are not processed through the PDF simulator. Select an email/PDF referral to re-simulate.',
      });
      return;
    }

    setLoadingReferralContext(true);
    setError(null);
    setSelectedReferralContext(null);
    // Clear stale files immediately so the previous selection's PDFs cannot be
    // accidentally submitted if attachment downloads fail for the new selection.
    setFiles([]);

    try {
      const ctx = await ApiService.getReferralSimulateContext(referral.id);
      if (resimulateSeqRef.current !== mySeq) return; // stale — newer selection won

      setSelectedReferralContext(ctx);
      // Pre-fill email context
      if (ctx.email_context.sender) {
        setSenderEmail(ctx.email_context.sender);
        setSenderName(ctx.email_context.sender.split('@')[0] || DEFAULTS.senderName);
      }
      if (ctx.email_context.subject) setSubject(ctx.email_context.subject);
      if (ctx.email_context.body_text) setBody(ctx.email_context.body_text);

      // Download all attachments as an all-settled operation; validate PDF magic bytes
      if (ctx.attachments.length > 0) {
        const results = await Promise.allSettled(
          ctx.attachments.map(async att => {
            const blob = await ApiService.fetchAttachmentAsBlob(att.id);
            // Validate PDF magic bytes to catch corrupt or non-PDF blobs before upload
            const header = await blob.slice(0, 4).arrayBuffer();
            const magic = new Uint8Array(header);
            const isPdf =
              magic[0] === 0x25 && magic[1] === 0x50 && magic[2] === 0x44 && magic[3] === 0x46; // %PDF
            if (!isPdf) throw new Error(`${att.filename} is not a valid PDF`);
            return new File([blob], att.filename, { type: 'application/pdf' });
          }),
        );

        if (resimulateSeqRef.current !== mySeq) return;

        const succeeded = results
          .filter((r): r is PromiseFulfilledResult<File> => r.status === 'fulfilled')
          .map(r => r.value);
        const failed = results
          .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
          .map(r => String(r.reason));

        if (failed.length > 0) {
          const msg =
            succeeded.length === 0
              ? `All attachments failed to load: ${failed.join('; ')} — upload your own PDF.`
              : `${failed.length} attachment(s) could not be loaded and were skipped: ${failed.join('; ')}`;
          setError({ message: msg });
        }

        if (succeeded.length > 0) {
          chooseFiles(succeeded);
        }
      }

      setSaveAsTemplateName(ctx.patient_name || 'Referral template');
    } catch (err) {
      if (resimulateSeqRef.current !== mySeq) return;
      setError({ message: err instanceof Error ? err.message : 'Failed to load referral context for re-simulation.' });
    } finally {
      if (resimulateSeqRef.current === mySeq) setLoadingReferralContext(false);
    }
  };

  const submitSaveAsTemplate = async () => {
    if (!selectedReferralContext || !saveAsTemplateName.trim()) return;
    setSaveAsTemplateSubmitting(true);
    setSaveAsTemplateError(null);
    try {
      const created = await ApiService.createTemplateFromReferral(
        selectedReferralContext.referral_id,
        saveAsTemplateName.trim(),
      );
      setTemplates(prev => [...prev, created]);
      setShowSaveAsTemplate(false);
      setSaveAsTemplateName('');
    } catch (err) {
      setSaveAsTemplateError(err instanceof Error ? err.message : 'Failed to save template.');
    } finally {
      setSaveAsTemplateSubmitting(false);
    }
  };

  const submitAddTemplate = async () => {
    if (!addTemplateFiles.length || !addTemplateName.trim()) return;
    setAddTemplateSubmitting(true);
    setAddTemplateError(null);
    try {
      const created = await ApiService.createTemplateReferral(
        addTemplateFiles,
        addTemplateName.trim(),
        addTemplateDesc.trim() || undefined,
        undefined,
        addTemplateSenderName.trim() || undefined,
        addTemplateSenderEmail.trim() || undefined,
        addTemplateSubject.trim() || undefined,
        addTemplateBody.trim() || undefined,
      );
      setTemplates(prev => [...prev, created]);
      setShowAddTemplate(false);
      setAddTemplateName('');
      setAddTemplateDesc('');
      setAddTemplateSenderName(DEFAULTS.senderName);
      setAddTemplateSenderEmail(DEFAULTS.senderEmail);
      setAddTemplateSubject(DEFAULTS.subject);
      setAddTemplateBody(DEFAULTS.body);
      setAddTemplateFiles([]);
    } catch (err) {
      setAddTemplateError(err instanceof Error ? err.message : 'Failed to upload template.');
    } finally {
      setAddTemplateSubmitting(false);
    }
  };

  const selectTemplate = async (template: TemplateReferral) => {
    setLoadingTemplate(true);
    setError(null);
    try {
      // Download all blobs in blob_paths (or fall back to single download)
      const paths = template.blob_paths && template.blob_paths.length > 0
        ? template.blob_paths
        : [null]; // null signals "use legacy download"

      const downloadedFiles: File[] = [];
      for (let i = 0; i < paths.length; i++) {
        const blob = await ApiService.downloadTemplateReferral(template.id);
        const suffix = paths.length > 1 ? ` (${i + 1})` : '';
        downloadedFiles.push(new File([blob], `${template.name}${suffix}.pdf`, { type: 'application/pdf' }));
        if (i < paths.length - 1) break; // Only one download endpoint for now; stop after first
      }
      chooseFiles(downloadedFiles);

      if (template.sender_name) setSenderName(template.sender_name);
      if (template.sender_email) setSenderEmail(template.sender_email);
      if (template.email_subject) setSubject(template.email_subject);
      if (template.email_body) setBody(template.email_body);
    } catch {
      setError({ message: `Failed to load template "${template.name}".` });
    } finally {
      setLoadingTemplate(false);
    }
  };

  const resultSources = useMemo(() => {
    const referral = result?.referral as Record<string, unknown> | undefined;
    const flatExtracted = flattenExtracted(result?.extracted as Record<string, unknown> | undefined);
    const flatRaw = flattenExtracted(result?.raw as Record<string, unknown> | undefined);
    return [referral, flatExtracted, flatRaw, result?.extracted, result?.extracted_data, result?.extracted_fields, result?.raw];
  }, [result]);
  const triage = (result?.triage || result?.triage_data ||
    (result?.referral as Record<string, unknown> | undefined)?.triage_data) as
    | Record<string, unknown>
    | undefined;
  const referralId =
    (typeof result?.referral_id === 'string' && result.referral_id) ||
    (typeof result?.referral?.id === 'string' && result.referral.id) ||
    (typeof result?.duplicate_referral_id === 'string' && result.duplicate_referral_id);

  const chooseFiles = (candidates: File[]) => {
    setResult(null);
    setDuplicate(false);
    const valid: File[] = [];
    for (const c of candidates) {
      const err = validatePdf(c);
      if (err) {
        setError({ message: err });
        return;
      }
      valid.push(c);
    }
    setFiles(valid);
    setError(null);
  };

  const addFiles = (newFiles: File[]) => {
    const valid: File[] = [];
    for (const c of newFiles) {
      const err = validatePdf(c);
      if (err) {
        setError({ message: err });
        return;
      }
      valid.push(c);
    }
    setFiles(prev => [...prev, ...valid]);
    setError(null);
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const submit = async () => {
    if (!files.length || loadingReferralContext) return;
    setSubmitting(true);
    setError(null);
    setDuplicate(false);
    try {
      const response = await ApiService.simulateReferral({
        files,
        senderName: senderName.trim(),
        senderEmail: senderEmail.trim(),
        subject: subject.trim(),
        body: body.trim(),
        pipelineMode,
      });
      setResult(response);
      setDuplicate(response.duplicate === true);
    } catch (caught) {
      if (caught instanceof SimulationApiError) {
        const isDuplicate =
          caught.status === 409 ||
          caught.details?.duplicate === true ||
          caught.details?.code === 'duplicate' ||
          caught.details?.error_code === 'DUPLICATE_RESOURCE';
        setDuplicate(isDuplicate);
        setError({ message: caught.message, stage: caught.stage });
        if (caught.details) setResult(caught.details as SimulationResponse);
      } else {
        setError({
          message: caught instanceof Error ? caught.message : 'The simulation could not be completed.',
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-container simulator-page">
      <header className="admin-page-header simulator-header">
        <div>
          <h1>PDF Referral Simulator</h1>
          <p className="admin-page-subtitle">
            Exercise the email referral extraction and triage pipeline with a controlled PDF.
          </p>
        </div>
        <span className="simulator-environment-label">Development tool</span>
      </header>

      <div className="simulator-panels">
        <form
          className="admin-card simulator-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <div className="simulator-section-heading">
            <span>1</span>
            <div>
              <h2>Simulation input</h2>
              <p>Select the referral PDF and adjust its email context.</p>
            </div>
          </div>

          {/* ── Re-simulate from existing referral ── */}
          <div className="simulator-resimulate-section" ref={searchContainerRef}>
            <h3 className="simulator-template-heading">Re-simulate existing referral</h3>
            <p className="simulator-resimulate-hint">
              Search by patient name or NHI to pre-fill from a referral already in the queue.
            </p>
            <div className="simulator-resimulate-search-row">
              <div className="simulator-resimulate-input-wrap">
                <FeatherIcon icon="search" size={15} className="simulator-resimulate-icon" aria-hidden="true" />
                <input
                  type="text"
                  className="simulator-resimulate-input"
                  placeholder="Patient name or NHI…"
                  value={referralSearch}
                  onChange={e => handleReferralSearchChange(e.target.value)}
                  onFocus={() => referralSearchResults.length > 0 && setReferralSearchOpen(true)}
                  disabled={submitting}
                  autoComplete="off"
                />
                {referralSearchLoading && (
                  <FeatherIcon icon="loader" size={13} className="simulator-spin simulator-resimulate-spinner" aria-hidden="true" />
                )}
              </div>
              {referralSearchOpen && referralSearchResults.length > 0 && (
                <ul className="simulator-resimulate-dropdown" role="listbox">
                  {referralSearchResults.map(r => {
                    const isSr = isSrReferral(r);
                    return (
                      <li
                        key={r.id}
                        role="option"
                        className={`simulator-resimulate-option${isSr ? ' simulator-resimulate-option--sr' : ''}`}
                        onMouseDown={() => void selectReferralForResimulate(r)}
                        title={isSr ? 'SR referrals use structured suburb/city and cannot be re-run through the PDF simulator' : undefined}
                      >
                        <span className="resimulate-name">
                          {r.patient_first_name} {r.patient_last_name}
                        </span>
                        {r.nhi_number && <span className="resimulate-nhi">{r.nhi_number}</span>}
                        {isSr && <span className="resimulate-sr-badge">SR</span>}
                        <span className="resimulate-status">{r.status}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            {loadingReferralContext && (
              <p className="simulator-resimulate-loading">
                <FeatherIcon icon="loader" size={13} className="simulator-spin" aria-hidden="true" />
                Loading referral context…
              </p>
            )}
            {selectedReferralContext && (
              <div className="simulator-resimulate-selected">
                <FeatherIcon icon="check-circle" size={14} aria-hidden="true" />
                <span>
                  Pre-filled from <strong>{selectedReferralContext.patient_name}</strong>
                  {selectedReferralContext.nhi_number && ` (${selectedReferralContext.nhi_number})`}
                  {' — '}
                  {selectedReferralContext.attachments.length} attachment
                  {selectedReferralContext.attachments.length !== 1 ? 's' : ''}
                </span>
                <button
                  type="button"
                  className="simulator-template-add-btn"
                  onClick={() => setShowSaveAsTemplate(v => !v)}
                >
                  <FeatherIcon icon="bookmark" size={13} aria-hidden="true" />
                  Save as template
                </button>
              </div>
            )}
            {showSaveAsTemplate && selectedReferralContext && (
              <div className="simulator-save-template-row">
                <input
                  type="text"
                  className="simulator-add-template-input"
                  placeholder="Template name *"
                  value={saveAsTemplateName}
                  onChange={e => setSaveAsTemplateName(e.target.value)}
                />
                <button
                  type="button"
                  className="simulator-template-save-btn"
                  disabled={!saveAsTemplateName.trim() || saveAsTemplateSubmitting}
                  onClick={() => void submitSaveAsTemplate()}
                >
                  {saveAsTemplateSubmitting ? 'Saving…' : 'Confirm save'}
                </button>
                {saveAsTemplateError && (
                  <p className="simulator-add-template-error">{saveAsTemplateError}</p>
                )}
              </div>
            )}
          </div>

          {/* ── Template picker ── */}
          <div className="simulator-template-picker">
            <div className="simulator-template-heading-row">
              <h3 className="simulator-template-heading">Template referrals</h3>
              <button
                type="button"
                className="simulator-template-add-btn"
                onClick={() => setShowAddTemplate(v => !v)}
                title="Upload a new template"
              >
                <FeatherIcon icon={showAddTemplate ? 'x' : 'plus'} size={15} aria-hidden="true" />
                {showAddTemplate ? 'Cancel' : 'Add template'}
              </button>
            </div>

            {showAddTemplate && (
              <div className="simulator-add-template-form">
                <input
                  type="text"
                  placeholder="Template name *"
                  value={addTemplateName}
                  onChange={e => setAddTemplateName(e.target.value)}
                  className="simulator-add-template-input"
                />
                <input
                  type="text"
                  placeholder="Description (optional)"
                  value={addTemplateDesc}
                  onChange={e => setAddTemplateDesc(e.target.value)}
                  className="simulator-add-template-input"
                />
                <div className="simulator-add-template-email-grid">
                  <input
                    type="text"
                    placeholder="Sender name"
                    value={addTemplateSenderName}
                    onChange={e => setAddTemplateSenderName(e.target.value)}
                    className="simulator-add-template-input"
                  />
                  <input
                    type="email"
                    placeholder="Sender email"
                    value={addTemplateSenderEmail}
                    onChange={e => setAddTemplateSenderEmail(e.target.value)}
                    className="simulator-add-template-input"
                  />
                </div>
                <input
                  type="text"
                  placeholder="Email subject"
                  value={addTemplateSubject}
                  onChange={e => setAddTemplateSubject(e.target.value)}
                  className="simulator-add-template-input"
                />
                <textarea
                  placeholder="Email body"
                  value={addTemplateBody}
                  onChange={e => setAddTemplateBody(e.target.value)}
                  className="simulator-add-template-input simulator-add-template-textarea"
                  rows={3}
                />
                <div className="simulator-add-template-file-row">
                  <input
                    ref={addTemplateInputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    multiple
                    style={{ display: 'none' }}
                    onChange={e => {
                      const picked = Array.from(e.target.files ?? []);
                      setAddTemplateFiles(prev => [...prev, ...picked]);
                    }}
                  />
                  <button
                    type="button"
                    className="simulator-template-add-btn"
                    onClick={() => addTemplateInputRef.current?.click()}
                  >
                    <FeatherIcon icon="upload" size={14} aria-hidden="true" />
                    {addTemplateFiles.length
                      ? `${addTemplateFiles.length} PDF${addTemplateFiles.length > 1 ? 's' : ''} selected`
                      : 'Choose PDF(s)'}
                  </button>
                  <button
                    type="button"
                    className="simulator-template-save-btn"
                    disabled={!addTemplateFiles.length || !addTemplateName.trim() || addTemplateSubmitting}
                    onClick={() => void submitAddTemplate()}
                  >
                    {addTemplateSubmitting ? 'Uploading…' : 'Save template'}
                  </button>
                </div>
                {addTemplateFiles.length > 0 && (
                  <ul className="simulator-file-list">
                    {addTemplateFiles.map((f, i) => (
                      <li key={i}>
                        <FeatherIcon icon="file-text" size={12} aria-hidden="true" />
                        <span>{f.name}</span>
                        <button type="button" onClick={() => setAddTemplateFiles(prev => prev.filter((_, j) => j !== i))} title="Remove">
                          <FeatherIcon icon="x" size={12} aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {addTemplateError && (
                  <p className="simulator-add-template-error">{addTemplateError}</p>
                )}
              </div>
            )}

            {templates.length === 0 && !showAddTemplate ? (
              <p className="simulator-template-empty">
                No templates yet. Use "Add template" to upload a referral PDF for reuse.
              </p>
            ) : templates.length > 0 ? (
              <div className="simulator-template-grid">
                {templates.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    className="simulator-template-card"
                    onClick={() => void selectTemplate(t)}
                    disabled={submitting || loadingTemplate}
                    title={t.description || t.name}
                  >
                    <FeatherIcon icon="file-text" size={18} aria-hidden="true" />
                    <span className="template-name">{t.name}</span>
                    {t.file_size && (
                      <span className="template-size">
                        {(t.file_size / 1024).toFixed(0)} KB
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ) : null}

            <p className="simulator-template-divider">— or upload your own PDF(s) below —</p>
          </div>

          {/* ── Drop zone + file list ── */}
          <div
            className={`simulator-dropzone${dragging ? ' simulator-dropzone--dragging' : ''}${
              files.length > 0 ? ' simulator-dropzone--selected' : ''
            }`}
            onDragEnter={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragging(false);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              addFiles(Array.from(event.dataTransfer.files));
            }}
          >
            <input
              ref={inputRef}
              id="simulator-pdf"
              type="file"
              accept="application/pdf,.pdf"
              multiple
              onChange={(event) => addFiles(Array.from(event.target.files ?? []))}
              disabled={submitting}
            />
            <FeatherIcon icon={files.length > 0 ? 'file-text' : 'upload-cloud'} size={32} aria-hidden="true" />
            {files.length > 0 ? (
              <>
                <strong>{files.length} PDF{files.length > 1 ? 's' : ''} selected</strong>
                <span>{files.reduce((sum, f) => sum + f.size, 0) < 1024 * 1024
                  ? `${(files.reduce((sum, f) => sum + f.size, 0) / 1024).toFixed(0)} KB total`
                  : `${(files.reduce((sum, f) => sum + f.size, 0) / (1024 * 1024)).toFixed(2)} MB total`}
                </span>
              </>
            ) : (
              <>
                <strong>Drop referral PDF(s) here</strong>
                <span>PDF only, up to 20 MB each</span>
              </>
            )}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={submitting}
            >
              Add PDF(s)
            </Button>
          </div>

          {files.length > 0 && (
            <ul className="simulator-file-list">
              {files.map((f, i) => (
                <li key={i}>
                  <FeatherIcon icon="file-text" size={13} aria-hidden="true" />
                  <span>{f.name} <span className="file-size-hint">({(f.size / 1024).toFixed(0)} KB)</span></span>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    title="Remove"
                    disabled={submitting}
                  >
                    <FeatherIcon icon="x" size={13} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* ── Email context ── */}
          <div className="simulator-email-grid">
            <label>
              Sender name
              <input
                value={senderName}
                onChange={(event) => setSenderName(event.target.value)}
                required
                disabled={submitting}
              />
            </label>
            <label>
              Sender email
              <input
                type="email"
                value={senderEmail}
                onChange={(event) => setSenderEmail(event.target.value)}
                required
                disabled={submitting}
              />
            </label>
            <label className="simulator-full-field">
              Subject
              <input
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                required
                disabled={submitting}
              />
            </label>
            <label className="simulator-full-field">
              Email body
              <textarea
                value={body}
                onChange={(event) => setBody(event.target.value)}
                rows={5}
                required
                disabled={submitting}
              />
            </label>
          </div>

          {/* ── Pipeline toggle ── */}
          <div className="simulator-pipeline-toggle">
            <span className="simulator-pipeline-label">Simulation mode</span>
            <div className="simulator-pipeline-options">
              <label className={`simulator-pipeline-option${pipelineMode === 'standalone' ? ' simulator-pipeline-option--active' : ''}`}>
                <input
                  type="radio"
                  name="pipeline_mode"
                  value="standalone"
                  checked={pipelineMode === 'standalone'}
                  onChange={() => setPipelineMode('standalone')}
                  disabled={submitting}
                />
                <FeatherIcon icon="monitor" size={14} aria-hidden="true" />
                <div>
                  <strong>Standalone</strong>
                  <span>Results shown here only — referral not added to queue</span>
                </div>
              </label>
              <label className={`simulator-pipeline-option${pipelineMode === 'pipeline' ? ' simulator-pipeline-option--active' : ''}`}>
                <input
                  type="radio"
                  name="pipeline_mode"
                  value="pipeline"
                  checked={pipelineMode === 'pipeline'}
                  onChange={() => setPipelineMode('pipeline')}
                  disabled={submitting}
                />
                <FeatherIcon icon="git-merge" size={14} aria-hidden="true" />
                <div>
                  <strong>Enter pipeline</strong>
                  <span>Referral appears in queue with SIMULATION badge</span>
                </div>
              </label>
            </div>
          </div>

          <Button
            type="submit"
            variant="primary"
            isLoading={submitting}
            disabled={!files.length}
            className="simulator-submit"
          >
            {submitting ? 'Running pipeline…' : 'Run simulation'}
          </Button>
        </form>

        {/* ── Results panel ── */}
        <section className="admin-card simulator-results" aria-live="polite" aria-busy={submitting}>
          <div className="simulator-section-heading">
            <span>2</span>
            <div>
              <h2>Pipeline result</h2>
              <p>Review extracted fields and the triage decision.</p>
            </div>
          </div>

          {submitting && (
            <div className="simulator-empty-state">
              <FeatherIcon icon="loader" className="simulator-spin" size={34} aria-hidden="true" />
              <strong>Processing referral</strong>
              <span>OCR, extraction, validation and triage may take a minute.</span>
            </div>
          )}

          {!submitting && error && (
            <div className="simulator-error" role="alert">
              <FeatherIcon icon="alert-circle" size={20} aria-hidden="true" />
              <div>
                <strong>Simulation failed{error.stage ? ` at ${error.stage}` : ''}</strong>
                <p>{error.message}</p>
                {duplicate && (
                  <Button type="button" variant="warning" size="sm" onClick={() => void submit()}>
                    Retry with new ID
                  </Button>
                )}
              </div>
            </div>
          )}

          {!submitting && !error && !result && (
            <div className="simulator-empty-state">
              <FeatherIcon icon="activity" size={34} aria-hidden="true" />
              <strong>Ready for a simulation</strong>
              <span>Results will appear here without leaving this page.</span>
            </div>
          )}

          {!submitting && result && !error && (
            <div className="simulator-result-content">
              <div className="simulator-success">
                <FeatherIcon icon="check-circle" size={20} aria-hidden="true" />
                <div>
                  <strong>{result.message || 'Referral processed successfully'}</strong>
                  <span>Final stage: {displayValue(result.stage || result.status)}</span>
                  {result.attachment_count && result.attachment_count > 1 && (
                    <span> · {result.attachment_count} PDFs processed</span>
                  )}
                  {result.pipeline_mode === 'standalone' && (
                    <span className="sim-badge sim-badge--neutral" style={{ marginLeft: 8 }}>Standalone</span>
                  )}
                  {result.pipeline_mode === 'pipeline' && (
                    <span className="sim-badge sim-badge--medium" style={{ marginLeft: 8 }}>In queue</span>
                  )}
                </div>
              </div>

              <section>
                <h3>Patient</h3>
                <dl className="simulator-result-grid">
                  <ResultField
                    label="Name"
                    value={`${displayValue(firstValue(resultSources, ['patient_first_name', 'first_name']))} ${displayValue(
                      firstValue(resultSources, ['patient_last_name', 'last_name']),
                    )}`}
                  />
                  <ResultField label="Date of birth" value={firstValue(resultSources, ['patient_dob', 'date_of_birth'])} />
                  <ResultField label="NHI" value={firstValue(resultSources, ['nhi_number', 'nhi', 'patient_nhi'])} />
                  <ResultField label="Contact" value={firstValue(resultSources, ['contact_email', 'contact_phone', 'patient_email', 'email', 'patient_phone', 'phone'])} />
                </dl>
              </section>

              <section>
                <h3>Referral</h3>
                <dl className="simulator-result-grid">
                  <ResultField label="Type" value={firstValue(resultSources, ['referral_type'])} />
                  <ResultField label="Body part" value={firstValue(resultSources, ['body_part'])} />
                  <ResultField label="Body side" value={firstValue(resultSources, ['body_side'])} />
                  <ResultField label="ACC claim" value={firstValue(resultSources, ['acc_claim_number', 'acc_number', 'referral_acc_number'])} />
                  <ResultField label="Injury date" value={firstValue(resultSources, ['date_of_injury'])} />
                  <ResultField label="Funder" value={firstValue(resultSources, ['funder'])} />
                  <ResultField label="Missing fields" value={firstValue(resultSources, ['missing_fields'])} />
                  <ResultField
                    label="Processing time"
                    value={
                      typeof result.processing_time_ms === 'number'
                        ? `${(result.processing_time_ms / 1000).toFixed(1)} seconds`
                        : undefined
                    }
                  />
                </dl>
              </section>

              <section>
                <h3>Referring provider</h3>
                <dl className="simulator-result-grid">
                  <ResultField label="Name" value={firstValue(resultSources, ['referring_provider_name', 'provider_name'])} />
                  <ResultField
                    label="Organisation"
                    value={firstValue(resultSources, [
                      'referring_provider_org',
                      'referring_provider_organization',
                      'provider_organization',
                    ])}
                  />
                  <ResultField label="Email" value={firstValue(resultSources, ['referring_provider_email', 'provider_email'])} />
                  <ResultField label="EDI" value={firstValue(resultSources, ['referring_provider_edi', 'provider_edi'])} />
                </dl>
              </section>

              <section className="simulator-triage">
                <h3>Triage</h3>
                <div className="simulator-triage-summary">
                  <TriageCategoryBadge
                    category={Number(firstValue([triage], ['triage_category_suggested', 'category', 'priority'])) || null}
                    variant="long"
                  />
                  <ResultField label="Theme" value={firstValue([triage], ['theme'])} />
                  <ResultField label="Complexity" value={firstValue([triage], ['complexity'])} />
                  <ResultField label="Confidence" value={firstValue([triage], ['confidence_percentage', 'confidence'])} />
                  <ResultField label="Red flags" value={firstValue([triage], ['red_flags'])} />
                  <ResultField label="Miscellaneous" value={firstValue([triage], ['is_miscellaneous'])} />
                  <ResultField label="Reasoning" value={firstValue([triage], ['reasoning'])} />
                </div>
              </section>

              {result.location && (() => {
                const loc = result.location as SimulationLocationResult;
                const confidence = loc.clinic_recommendation_confidence;
                const isLowConfidence = confidence !== null && confidence !== undefined && confidence < 40;
                const isPracticeFallback = loc.patient_location_source === 'practice_address';
                return (
                  <section className="simulator-location">
                    <h3>Location &amp; Clinic Routing</h3>

                    {isPracticeFallback && (
                      <div className="sim-banner sim-banner--warning">
                        Patient address was not found — location based on referring practice address.
                      </div>
                    )}
                    {isLowConfidence && (
                      <div className="sim-banner sim-banner--alert">
                        Low confidence — manual clinic assignment recommended.
                      </div>
                    )}

                    <dl className="simulator-result-grid">
                      <div className="simulator-result-field">
                        <dt>Extraction status</dt>
                        <dd>
                          {loc.patient_location_status === 'extracted' && <span className="sim-badge sim-badge--high">Extracted</span>}
                          {loc.patient_location_status === 'not_found' && <span className="sim-badge sim-badge--medium">Not found in text</span>}
                          {loc.patient_location_status === 'extraction_failed' && <span className="sim-badge sim-badge--low">Extraction failed</span>}
                          {!loc.patient_location_status && <span className="sim-badge sim-badge--neutral">Unknown</span>}
                        </dd>
                      </div>
                      <ResultField label="Patient suburb" value={loc.patient_suburb} />
                      <ResultField label="Patient city" value={loc.patient_city} />
                      <div className="simulator-result-field">
                        <dt>Location source</dt>
                        <dd><SourceBadge source={loc.patient_location_source} /></dd>
                      </div>
                      <div className="simulator-result-field">
                        <dt>Location confidence</dt>
                        <dd><span className={`sim-badge sim-badge--${loc.patient_location_confidence === 'high' ? 'high' : loc.patient_location_confidence === 'medium' ? 'medium' : 'low'}`}>{loc.patient_location_confidence || 'None'}</span></dd>
                      </div>
                      {isPracticeFallback && (
                        <>
                          <ResultField label="Practice suburb" value={loc.practice_suburb} />
                          <ResultField label="Practice city" value={loc.practice_city} />
                        </>
                      )}
                      {!isPracticeFallback && (loc.practice_suburb || loc.practice_city) && (
                        <div className="simulator-result-field simulator-result-field--dimmed">
                          <dt>Practice suburb (stored)</dt>
                          <dd>{loc.practice_suburb || '—'}, {loc.practice_city || '—'}</dd>
                        </div>
                      )}
                      <ResultField
                        label="Coordinates"
                        value={
                          loc.referral_latitude && loc.referral_longitude
                            ? `${loc.referral_latitude.toFixed(4)}, ${loc.referral_longitude.toFixed(4)}`
                            : null
                        }
                      />
                      <div className="simulator-result-field">
                        <dt>Geocoding match</dt>
                        <dd><GeoMatchBadge matchType={loc.geocoding_match_type} /></dd>
                      </div>
                      <ResultField label="Recommended clinic" value={loc.recommended_clinic_name} />
                      <ResultField
                        label="Distance"
                        value={loc.clinic_recommendation_distance_km != null ? `${loc.clinic_recommendation_distance_km.toFixed(1)} km` : null}
                      />
                      <div className="simulator-result-field">
                        <dt>Recommendation confidence</dt>
                        <dd>
                          <ConfidenceBadge score={confidence ?? null} />
                          {confidence !== null && confidence !== undefined && (
                            <div className="sim-confidence-bar">
                              <div
                                className={`sim-confidence-bar__fill ${confidence >= 70 ? 'high' : confidence >= 40 ? 'medium' : 'low'}`}
                                style={{ width: `${Math.min(100, confidence)}%` }}
                              />
                            </div>
                          )}
                        </dd>
                      </div>
                    </dl>
                  </section>
                );
              })()}

              {result.referrer_type && (() => {
                const rt = result.referrer_type as SimulationReferrerTypeResult;
                const confidence = rt.referrer_type_confidence;
                const methodLabels: Record<string, string> = {
                  EXACT: 'Exact',
                  KEYWORD: 'Keyword',
                  SQL_FUZZY: 'Fuzzy',
                  AI: 'AI',
                  NONE: 'None',
                };
                return (
                  <section className="simulator-location">
                    <h3>Referrer Type</h3>
                    <dl className="simulator-result-grid">
                      <ResultField label="Source text" value={rt.source_text} />
                      <ResultField
                        label="Resolved type"
                        value={rt.referrer_type_display_name || rt.referrer_type}
                      />
                      <div className="simulator-result-field">
                        <dt>Match method</dt>
                        <dd>
                          {rt.referrer_type_match_method
                            ? methodLabels[rt.referrer_type_match_method] || rt.referrer_type_match_method
                            : 'Not resolved'}
                        </dd>
                      </div>
                      <div className="simulator-result-field">
                        <dt>Confidence</dt>
                        <dd><ConfidenceBadge score={confidence ?? null} /></dd>
                      </div>
                      <ResultField label="Status" value={rt.referrer_type_status} />
                    </dl>
                  </section>
                );
              })()}

              {referralId && result.pipeline_mode === 'pipeline' && (
                <Link className="simulator-queue-link" to={`/queue?referral=${encodeURIComponent(referralId)}`}>
                  Open referral in queue
                  <FeatherIcon icon="arrow-right" size={16} aria-hidden="true" />
                </Link>
              )}

              <details className="simulator-raw-json">
                <summary>Raw pipeline response</summary>
                <pre>{JSON.stringify(result, null, 2)}</pre>
              </details>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
